import React, { useEffect, useState } from 'react'
import {
  Box, Grid, Typography, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Select, MenuItem, FormControl, InputLabel, CircularProgress,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import withLayout from '../../hocs/Layout'
import PageTitle from '../../common/PageTitle'
import { apiConstants, getGeneric, getGenericBlob } from '../../common/constants'
import { useGetMerchantQuery } from '../../redux/commonRTK'
import { getMerchant } from '../../helpers/ReduxHelpers'
import { formatNumber, filterByProp } from '../../helpers/UtilityHelper'
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography'
import { NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, SUCCESS, ERROR } from '../../styles/foundation/colors'
import { SHADOW } from '../../styles/foundation/effects'
import { pageContainerSx } from '../../styles/patterns'

// Phase 1 of the merchant serviceability feature — "your reach vs. Prozo's reach", by
// zone, for one pickup / service type / payment mode at a time. Deliberately a fresh
// component rather than a revival of the old (commented-out) Serviceability dashboard:
// that page's data model was platform-wide multi-mode analytics from the admin-gated
// serviceability-intel APIs, which merchants can never call — this page's data model is
// allocation-vs-Prozo, built entirely from merchant-safe endpoints.

const cardSx = { bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', boxShadow: SHADOW.card, p: { xs: 2, md: 2.5 } }
const heroSx = {
  background: `linear-gradient(135deg, ${NAVY} 0%, #16213e 60%, #0f3460 100%)`,
  borderRadius: '14px', p: { xs: 2.5, md: 3 }, mb: 2.5,
}
const statSx = { bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '10px', p: 1.75, height: '100%' }
const gapStatSx = { ...statSx, borderColor: `${ERROR}55`, bgcolor: `${ERROR}0a` }

function StatCard({ label, value, sub, gap }) {
  return (
    <Box sx={gap ? gapStatSx : statSx}>
      <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xl'], fontWeight: WEIGHT.bold, color: gap ? ERROR : NAVY, mt: 0.3 }}>{value}</Typography>
      {sub && <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.2 }}>{sub}</Typography>}
    </Box>
  )
}

function ShareBar({ pct }) {
  const width = pct == null ? 0 : Math.min(100, pct)
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
      <Box sx={{ flex: 1, height: 6, borderRadius: '4px', bgcolor: '#eef0f3', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${width}%`, bgcolor: PRIMARY_BLUE, borderRadius: '4px' }} />
      </Box>
      <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY, fontWeight: WEIGHT.semibold, minWidth: 36, textAlign: 'right' }}>
        {pct == null ? '—' : `${pct}%`}
      </Typography>
    </Box>
  )
}

function usePickupOptions() {
  const merchant = getMerchant()
  const { data, isLoading } = useGetMerchantQuery({ id: merchant?.id })
  const pickupLocations = filterByProp(data?.pickup_details, 'is_deleted') || []
  const options = pickupLocations
    .filter((p) => p.status === 'ACTIVE' && p.address_type === 'PICKUP' && p.pincode)
    // key is the location id (unique) — a merchant can have several named warehouses at
    // the same pincode, but the API call itself only ever needs the pincode, so `value`
    // stays the pincode even when several options share one.
    .map((p) => ({
      key: p.id || p.pincode,
      value: p.pincode,
      label: `${p.pincode}${p.city ? ' — ' + p.city : ''}${p.name ? ' (' + p.name + ')' : ''}`,
    }))
  return { options, isLoading }
}

function useServiceTypeOptions() {
  const [options, setOptions] = useState(null)
  useEffect(() => {
    let active = true
    getGeneric(apiConstants.SERVICEABILITY_MERCHANT_SERVICE_TYPES)
      .then((res) => { if (active) setOptions(res?.data?.responseObj || []) })
      .catch(() => { if (active) setOptions([]) })
    return () => { active = false }
  }, [])
  return options
}

const CATEGORY_LABELS = {
  FORWARD: 'Forward', REVERSE: 'Reverse', HIGH_VALUE: 'High value', SDD_NDD: 'Same-day / Next-day', HYPERLOCAL: 'Hyperlocal',
}

// Covers every active service type at once — a separate fetch from the zone comparison
// above (which is one service type at a time), so it only depends on pickup + payment
// mode, never on the service-type selector.
function useModesCoverage(pickupPincode, paymentMode) {
  const [modes, setModes] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  useEffect(() => {
    if (!pickupPincode) return undefined
    let active = true
    setLoading(true)
    setError(null)
    getGeneric(apiConstants.SERVICEABILITY_MERCHANT_COVERAGE_MODES, { pickupPincode, paymentMode })
      .then((res) => { if (active) setModes(res?.data?.responseObj || null) })
      .catch((e) => { if (active) { setError(e?.response?.data?.message || 'Failed to load modes'); setModes(null) } })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [pickupPincode, paymentMode])
  return { modes, loading, error }
}

function Coverage() {
  const { options: pickupOptions, isLoading: pickupsLoading } = usePickupOptions()
  const serviceTypeOptions = useServiceTypeOptions()

  const [pickupPincode, setPickupPincode] = useState('')
  const [serviceType, setServiceType] = useState('')
  const [paymentMode, setPaymentMode] = useState('PREPAID')
  const [coverage, setCoverage] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [downloading, setDownloading] = useState(null)
  const { modes, loading: modesLoading, error: modesError } = useModesCoverage(pickupPincode, paymentMode)

  // Default selections once their option lists arrive.
  useEffect(() => {
    if (!pickupPincode && pickupOptions.length > 0) setPickupPincode(pickupOptions[0].value)
  }, [pickupOptions, pickupPincode])
  useEffect(() => {
    if (!serviceType && serviceTypeOptions && serviceTypeOptions.length > 0) setServiceType(serviceTypeOptions[0].code)
  }, [serviceTypeOptions, serviceType])

  useEffect(() => {
    if (!pickupPincode || !serviceType) return undefined
    let active = true
    setLoading(true)
    setError(null)
    getGeneric(apiConstants.SERVICEABILITY_MERCHANT_COVERAGE_ZONES, { pickupPincode, serviceType, paymentMode })
      .then((res) => { if (active) setCoverage(res?.data?.responseObj || null) })
      .catch((e) => { if (active) { setError(e?.response?.data?.message || 'Failed to load coverage'); setCoverage(null) } })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [pickupPincode, serviceType, paymentMode])

  const handleDownload = async (kind) => {
    if (!pickupPincode || !serviceType) return
    setDownloading(kind)
    try {
      const endpoint = kind === 'zones'
        ? apiConstants.SERVICEABILITY_MERCHANT_COVERAGE_ZONES_CSV
        : apiConstants.SERVICEABILITY_MERCHANT_COVERAGE_PINCODES_CSV
      const res = await getGenericBlob(endpoint, { pickupPincode, serviceType, paymentMode })
      const href = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }))
      const link = document.createElement('a')
      link.href = href
      link.setAttribute('download', `coverage-by-${kind === 'zones' ? 'zone' : 'pincode'}-${serviceType}-${paymentMode}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(href)
    } catch (e) {
      setError('Failed to download CSV')
    } finally {
      setDownloading(null)
    }
  }

  const selectSx = { fontFamily: FONT, fontSize: SIZE.xs, bgcolor: WHITE }

  return (
    <Box sx={pageContainerSx}>
      <PageTitle>Serviceability Coverage</PageTitle>

      <Box sx={heroSx}>
        <Typography sx={{ color: WHITE, fontWeight: WEIGHT.bold, fontSize: SIZE.xl, fontFamily: FONT, mb: 0.3 }}>
          Your reach vs. Prozo&apos;s reach
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: SIZE.xs, fontFamily: FONT }}>
          Pincode coverage under your current courier allocation, compared zone by zone against everything Prozo can offer for the same service type.
        </Typography>
      </Box>

      <Alert severity="warning" sx={{ borderRadius: '10px', mb: 2.5, fontFamily: FONT, fontSize: SIZE.xs }}>
        <strong>Scope:</strong> these numbers are for D2C / B2C shipments. B2B / PTL serviceability runs on a different (lane / zone) model and isn&apos;t shown here.
      </Alert>

      <Box sx={{ ...cardSx, mb: 2.5 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small" disabled={pickupsLoading || pickupOptions.length === 0}>
              <InputLabel sx={selectSx}>Pickup location</InputLabel>
              <Select
                value={pickupPincode}
                label="Pickup location"
                sx={selectSx}
                onChange={(e) => setPickupPincode(e.target.value)}
              >
                {pickupOptions.map((o) => (
                  <MenuItem key={o.key} value={o.value} sx={{ fontFamily: FONT, fontSize: SIZE.xs }}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={5}>
            <FormControl fullWidth size="small" disabled={!serviceTypeOptions || serviceTypeOptions.length === 0}>
              <InputLabel sx={selectSx}>Service type</InputLabel>
              <Select
                value={serviceType}
                label="Service type"
                sx={selectSx}
                onChange={(e) => setServiceType(e.target.value)}
              >
                {(serviceTypeOptions || []).map((o) => (
                  <MenuItem key={o.code} value={o.code} sx={{ fontFamily: FONT, fontSize: SIZE.xs }}>{o.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel sx={selectSx}>Payment mode</InputLabel>
              <Select
                value={paymentMode}
                label="Payment mode"
                sx={selectSx}
                onChange={(e) => setPaymentMode(e.target.value)}
              >
                <MenuItem value="PREPAID" sx={{ fontFamily: FONT, fontSize: SIZE.xs }}>Prepaid</MenuItem>
                <MenuItem value="COD" sx={{ fontFamily: FONT, fontSize: SIZE.xs }}>COD</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        {pickupOptions.length === 0 && !pickupsLoading && (
          <Alert severity="info" sx={{ mt: 2, borderRadius: '8px', fontFamily: FONT, fontSize: SIZE.xs }}>
            No active pickup locations found on your account.
          </Alert>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ borderRadius: '10px', mb: 2.5, fontFamily: FONT, fontSize: SIZE.xs }}>{error}</Alert>
      )}

      {!loading && !error && coverage && (
        <>
          {(coverage.notes || []).map((n, i) => (
            <Alert key={i} severity="info" icon={false} sx={{ borderRadius: '10px', mb: 1.5, fontFamily: FONT, fontSize: SIZE.xs }}>{n}</Alert>
          ))}

          {!coverage.zoneDataAvailable ? (
            <Box sx={{ ...cardSx, textAlign: 'center', py: 5 }}>
              <Typography sx={{ fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.md, color: NAVY }}>
                Zone data pending for this pickup
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, mt: 0.5 }}>
                {pickupPincode} hasn&apos;t been mapped into the zone map yet — coverage can&apos;t be broken down by zone until it is.
              </Typography>
            </Box>
          ) : (
            <>
              <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                <Grid item xs={6} sm={3}>
                  <StatCard label="Your reach" value={formatNumber(coverage.summary?.yourPins)}
                    sub={`${coverage.summary?.yourCourierCount || 0} courier${coverage.summary?.yourCourierCount === 1 ? '' : 's'}`} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatCard label="Prozo reach" value={formatNumber(coverage.summary?.prozoPins)}
                    sub={`${coverage.summary?.prozoCourierCount || 0} courier${coverage.summary?.prozoCourierCount === 1 ? '' : 's'}`} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatCard label="Gap" value={formatNumber(coverage.summary?.gapPins)} gap sub="pincodes Prozo can add" />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatCard label="Your share" value={coverage.summary?.sharePct != null ? `${coverage.summary.sharePct}%` : '—'} sub="of what Prozo offers" />
                </Grid>
              </Grid>

              <Box sx={cardSx}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8f9fc' }}>
                        {['Zone', 'Zone size', 'Your reach', 'Prozo reach', 'Gap', 'Share'].map((h, i) => (
                          <TableCell key={h} align={i === 0 ? 'left' : i === 5 ? 'left' : 'right'}
                            sx={{ fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE['2xs'], color: SUBTLE, textTransform: 'uppercase' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(coverage.zones || []).map((z) => (
                        <TableRow key={z.zone} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                          <TableCell sx={{ fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.sm, color: NAVY }}>{z.zoneName || z.zone}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: FONT, fontSize: SIZE.sm }}>{formatNumber(z.zonePins)}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: FONT, fontSize: SIZE.sm }}>{formatNumber(z.yourPins)}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: FONT, fontSize: SIZE.sm }}>{formatNumber(z.prozoPins)}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: FONT, fontSize: SIZE.sm, color: z.gapPins > 0 ? ERROR : SUCCESS, fontWeight: WEIGHT.semibold }}>{formatNumber(z.gapPins)}</TableCell>
                          <TableCell sx={{ minWidth: 140 }}><ShareBar pct={z.sharePct} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, mt: 2.5, flexWrap: 'wrap' }}>
                <LoadingButton
                  loading={downloading === 'zones'} onClick={() => handleDownload('zones')}
                  variant="outlined" startIcon={<DownloadOutlinedIcon />}
                  sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, borderRadius: '999px' }}>
                  Download zone summary (CSV)
                </LoadingButton>
                <LoadingButton
                  loading={downloading === 'pincodes'} onClick={() => handleDownload('pincodes')}
                  variant="outlined" startIcon={<DownloadOutlinedIcon />}
                  sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, borderRadius: '999px' }}>
                  Download pincode detail (CSV)
                </LoadingButton>
              </Box>
            </>
          )}
        </>
      )}

      <Box sx={{ mt: 4 }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.lg, color: NAVY, mb: 0.3 }}>
          Modes — current vs. available
        </Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, mb: 1.5 }}>
          Every mode Prozo offers, compared to what you use. Same-day and next-day reflect the pickup location selected above; the other modes reflect a courier&apos;s total reach and aren&apos;t pickup-specific.
        </Typography>

        {modesLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
        )}
        {!modesLoading && modesError && (
          <Alert severity="error" sx={{ borderRadius: '10px', fontFamily: FONT, fontSize: SIZE.xs }}>{modesError}</Alert>
        )}
        {!modesLoading && !modesError && modes && (
          <>
            {(modes.notes || []).map((n, i) => (
              <Alert key={i} severity="info" icon={false} sx={{ borderRadius: '10px', mb: 1.5, fontFamily: FONT, fontSize: SIZE.xs }}>{n}</Alert>
            ))}
            <Box sx={cardSx}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8f9fc' }}>
                      {['Mode', 'Category', 'In your allocation', 'Your reach', 'Prozo reach', 'Gap', 'Share'].map((h, i) => (
                        <TableCell key={h} align={i <= 1 ? 'left' : i === 6 ? 'left' : 'right'}
                          sx={{ fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE['2xs'], color: SUBTLE, textTransform: 'uppercase' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(modes.modes || []).map((m) => {
                      const unavailable = m.dataSource === 'UNAVAILABLE'
                      return (
                        <TableRow key={m.code} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                          <TableCell sx={{ fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.sm, color: NAVY }}>{m.name}</TableCell>
                          <TableCell sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE }}>{CATEGORY_LABELS[m.category] || m.category}</TableCell>
                          <TableCell sx={{ fontFamily: FONT, fontSize: SIZE.sm, color: m.inAllocation ? SUCCESS : SUBTLE, fontWeight: m.inAllocation ? WEIGHT.semibold : WEIGHT.regular }}>
                            {unavailable ? '—' : (m.inAllocation ? `Yes (${m.yourCourierCount})` : 'No')}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: FONT, fontSize: SIZE.sm }}>{unavailable ? '—' : formatNumber(m.yourPins)}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: FONT, fontSize: SIZE.sm }}>{unavailable ? '—' : formatNumber(m.prozoPins)}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: FONT, fontSize: SIZE.sm, color: !unavailable && m.gapPins > 0 ? ERROR : SUCCESS, fontWeight: WEIGHT.semibold }}>
                            {unavailable ? '—' : formatNumber(m.gapPins)}
                          </TableCell>
                          <TableCell sx={{ minWidth: 140 }}>{unavailable ? <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>No data source yet</Typography> : <ShareBar pct={m.sharePct} />}</TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}

export default withLayout(Coverage)
